import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import Button from "shared/components/buttons/Button";
import Icon from "shared/components/Icon";
import { ICONS } from "shared/components/Icon";
import InputText from "shared/components/inputs/InputText";
import InputSelect from "shared/components/inputs/InputSelect";
import uploadImage from "helpers/getPublicImageUrl";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import axios from "axios";
import OkWarning from "shared/components/OkWarning";
import { snackbarSuccess } from "shared/redux/actions/Snackbar";

import "./style.scss";

const Invitation = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { osbn } = useParams();
    const fileRef = useRef(null);
    const [htmlContent, setHtmlContent] = useState("");
    const { control, watch, handleSubmit, errors } = useForm({
        defaultValues: {
            htmlContent: htmlContent,
        },
    });
    const { books } = useSelector(state => state.books);
    const { profile } = useSelector(state => state.user);

    const book = books[osbn];
    const [showForm, setShowForm] = useState(false);
    const [customTemplate, setCustomTemplate] = useState(true);
    const [htmlTemplate, setHtmlTemplate] = useState(false);
    const [coverImage, setCoverImage] = useState(null);
    const [description, setDescription] = useState("");
    const [language, setLanguage] = useState("en");
    const [initialState, setInitialState] = useState({});
    const [approvalStatus, setApprovalStatus] = useState("");
    const isDev = (!process.env.NODE_ENV || process.env.NODE_ENV === "development");
    const [showConfirmRemove, setShowConfirmRemove] = useState(false);
    const [showConfirmNew, setShowConfirmNew] = useState(false);


    const API_BASE_URL = isDev
        ? "http://localhost:1337"
        : window.location.href.includes("acceptance")
            ? "https://acceptance.okcomply.com"
            : "https://my.okcomply.com";

    const prefillForm = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/invites/invitation-template/${book.id}`);
            const template = response.data;

            const initialValues = {
                coverImage: template.logoUrl,
                customTemplate: template.templateType === "custom",
                htmlTemplate: template.templateType === "html",
                htmlContent: template.htmlContent || "",
                description: template.description || "",
                language: template.language || "en"
            };

            setApprovalStatus(template.approvalStatus);
            setCoverImage(initialValues.coverImage);
            setCustomTemplate(initialValues.customTemplate);
            setHtmlTemplate(initialValues.htmlTemplate);
            setHtmlContent(initialValues.htmlContent);
            setDescription(initialValues.description);
            setLanguage(initialValues.language);

            setInitialState(initialValues);
        } catch (error) {
            console.error("Failed to prefill form:", error.response?.data || error.message);
        }
    };

    useEffect(() => {

        if (htmlTemplate) {

            handleSaveClick();
        }
    }, [approvalStatus]);

    useEffect(() => {
        prefillForm();
    }, []);

    const onFileSelected = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setCoverImage(URL.createObjectURL(file));

        try {

            const uploadedUrl = await uploadImage(file);

            setCoverImage(uploadedUrl);
        } catch (err) {
            console.error("Upload failed", err);
        }
    };

    const handleCreateClick = () => {
        setShowForm(true);
    };

    const watchedHtmlContent = watch("htmlContent");

    useEffect(() => {
        setHtmlContent(watchedHtmlContent);
    }, [watchedHtmlContent]);
    const handleSaveClick = async () => {


        let effectiveApprovalStatus = approvalStatus;

        if (htmlContent && approvalStatus === "") {
            effectiveApprovalStatus = "PENDING";
            setApprovalStatus("PENDING");
            return;
        }

        const payload = {
            bookId: book.id,
            logoUrl: coverImage,
            description,
            language: language,
            htmlContent,
            templateType: htmlTemplate ? "html" : "custom",
            approvalStatus: effectiveApprovalStatus,
            approvalMessage: "Ready for review",
            ownerEmail: profile.email,
            ownerName: profile.name
        };

        dispatch(snackbarSuccess(t("book.edit.invitation.snackbar.success")));
        setShowForm(false);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/invites/invitation-template-create`,
                payload
            );
            return response.data;
        } catch (error) {
            console.error("Error saving invitation:", error);
            throw error;
        }
    };

    const handleCustomTemplateChange = () => {
        setCustomTemplate(true);
        setHtmlTemplate(false);
    };

    const handleHtmlTemplateChange = () => {
        setHtmlTemplate(true);
        setCustomTemplate(false);
    };

    const selectFile = () => {
        fileRef.current.click();
    };

    const renderCover = () => {
        return (
            <div className="cover-logo">
                <input
                    className="hidden-file"
                    type="file"
                    onChange={onFileSelected}
                    ref={fileRef}
                />
                <img src={coverImage || require("assets/img/books-placeholder.png")} alt="Cover" />
                <div className="image-change">
                    <Icon
                        name={ICONS.CAMERA}
                        onClick={selectFile}
                    />
                </div>
            </div>
        );
    };

    const renderConfirmRemove = () => {
        return (
            <OkWarning
                title={t("book.edit.invitation.html.removal.title")}
                message={t("book.edit.invitation.html.removal.text")}
                onConfirm={() => {
                    setApprovalStatus("");
                    setHtmlContent("");

                    setShowConfirmRemove(false);
                }}
                onCancel={() => {
                    setShowConfirmRemove(false);
                }}
            />
        );
    };

    const renderConfirmNew = () => {
        return (
            <OkWarning
                title={t("book.edit.invitation.html.warning.title")}
                message={t("book.edit.invitation.html.warning.text")}
                onConfirm={() => {
                    setApprovalStatus("");
                    setHtmlContent("");

                    setShowConfirmNew(false);
                }}
                onCancel={() => {
                    setShowConfirmNew(false);
                }}
            />
        );
    };

    const statusButtons = {
        APPROVED: {
            label: "APPROVED",
            icon: ICONS.CHECK,
            style: "",
            danger: false,
            secondary: false
        },
        REJECTED: {
            label: "REJECTED",
            icon: ICONS.CROSS,
            style: "danger",
            danger: true,
            secondary: false
        },
        PENDING: {
            label: "PENDING",
            icon: ICONS.CLOCK,
            style: "secondary",
            danger: false,
            secondary: true
        },
        CANCELED: {
            label: "CANCELED",
            icon: ICONS.CROSS,
            style: "secondary",
            danger: false,
            secondary: true
        }
    };

    const buttonConfig = statusButtons[approvalStatus] || null;



    return (
        <div className="section-general">
            <h2>{t("book.edit.invitation.title")}</h2>
            <div className="divider" />
            <div className="info-text">
                {t("book.edit.invitation.description")}
            </div>
            <div className="spacer" />

            {!showForm && (
                <div className="container-buttons">
                    <Button
                        spacedLeft
                        outlined
                        iconRight={ICONS.PENCIL}
                        label={t("book.edit.invitation.button.edit")}
                        onClick={handleCreateClick}
                    />
                </div>
            )}

            {showForm && (
                <>
                    <div className="divider" />
                    <div className="checkbox-container">
                        <FormControlLabel
                            label={<span className="non-selectable">{t("book.edit.invitation.checkbox.custom")}</span>}
                            control={
                                <Checkbox
                                    checked={customTemplate}
                                    onChange={handleCustomTemplateChange}
                                    name="customTemplate"
                                    color="primary"
                                />
                            }
                        />
                        <FormControlLabel
                            label={<span className="non-selectable">{t("book.edit.invitation.checkbox.html")}</span>}
                            control={
                                <Checkbox
                                    checked={htmlTemplate}
                                    onChange={handleHtmlTemplateChange}
                                    name="htmlTemplate"
                                    color="primary"
                                />
                            }
                        />
                    </div>
                    <div className="divider" />

                    {customTemplate && (
                        <div className="cols-m">
                            {renderCover()}
                            <div className="col">
                                <InputText
                                    name="description"
                                    label={t("book.edit.description")}
                                    defaultValue={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    control={control}
                                    rules={{ required: true }}
                                    error={errors.description}
                                    multiline
                                    dataTestId="description"
                                />
                                <InputSelect
                                    items={[
                                        { value: "en", label: t("book.edit.invitation.english") },
                                        { value: "nl", label: t("book.edit.invitation.dutch") }
                                    ]}
                                    label={t("book.edit.invitation.template.language")}
                                    name="language"
                                    value={language}
                                    onChange={({ target }) => setLanguage(target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {htmlTemplate && (
                        <div className="col">
                            {["PENDING", "APPROVED", "REJECTED"].includes(approvalStatus) && (
                                <>
                                    <div className="row status-row">
                                        <label className="status-label">
                                            {t("book.edit.invitation.template.status")}
                                        </label>
                                        <div className="button-container">
                                            {buttonConfig ? (
                                                <div className="button-disabled-wrapper">
                                                    <Button
                                                        iconRight={buttonConfig.icon}
                                                        label={buttonConfig.label}
                                                        danger={buttonConfig.danger}
                                                        secondary={buttonConfig.secondary}
                                                        onClick={() => { }}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="divider" />
                                </>
                            )}
                            <InputText
                                name="htmlContent"
                                label={t("book.edit.invitation.htmlContent")}
                                defaultValue={htmlContent}
                                control={control}
                                rules={{
                                    validate: (value) => {
                                        if (
                                            approvalStatus === "PENDING" ||
                                            approvalStatus === "APPROVED" ||
                                            approvalStatus === "REJECTED"
                                        ) {
                                            return true;
                                        }

                                        if (!value) {
                                            return t("formValidation.required");
                                        }

                                        if (!value.includes("{{URL}}")) {
                                            return i18n.language === "nl"
                                                ? "Je moet een link toevoegen met de {{URL}} (<a href='{{URL}}'>Voorbeeld</a>)"
                                                : "You must include a link using the {{URL}} (<a href='{{URL}}'>Example</a>)";
                                        }

                                        return true;
                                    }
                                }}

                                error={errors.htmlContent}
                                multiline
                                dataTestId="htmlContent"
                                readOnly={
                                    approvalStatus === "PENDING" ||
                                    approvalStatus === "APPROVED" ||
                                    approvalStatus === "REJECTED"
                                }
                            />
                            <div className="template-status">

                                <div className="container-buttons">

                                    {approvalStatus === "PENDING" && (
                                        <Button
                                            spacedLeft
                                            iconRight={ICONS.CROSS}
                                            outlined
                                            label={t("book.edit.invitation.html.removal.button")}
                                            onClick={() => setShowConfirmRemove(true)}
                                        />
                                    )}

                                    {(approvalStatus === "APPROVED" || approvalStatus === "REJECTED") && (
                                        <Button
                                            spacedLeft
                                            iconRight={ICONS.CROSS}
                                            outlined
                                            label={t("book.edit.invitation.html.renew.button")}
                                            onClick={() => setShowConfirmNew(true)}
                                        />
                                    )}

                                </div>
                            </div>
                        </div>
                    )}
                    {showConfirmRemove && renderConfirmRemove()}
                    {showConfirmNew && renderConfirmNew()}


                    <div className="divider" />
                    <div className="button-container">
                        <Button
                            label={t("button.save.label")}
                            onClick={handleSubmit(handleSaveClick)}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default Invitation;
